import React, { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { Loader2 } from 'lucide-react';

interface ClientActiveRow {
  id: string;
  name: string;
  email: string;
  soldePartenaire: number;
  totalCollecte: number;
  commissionVotAfrica: number;
  totalRetraits: number;
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(amount);
};

// Fonction pour calculer le revenu ajusté - identique à celle dans ClientFinances
const calculateAdjustedRevenue = (rawRevenue: number, transactionsCount: number) => {
  // Calculer le taux de charge basé sur le nombre de transactions et le revenu total
  const T_eff = Math.max(transactionsCount || 30, 30);
  const charge_rate = Math.min(10, 
    3 + // taux de base
    5 * Math.sqrt(T_eff / 500) + // composante transactions
    5 * (Math.log(rawRevenue + 1) / Math.log(1000001)) // composante revenu
  );

  // Calculer le revenu ajusté après application des charges
  const adjusted_revenue = rawRevenue;
  
  // Arrondir au multiple de 100 le plus proche pour plus de discrétion
  return Math.round(adjusted_revenue / 100) * 100;
};

export const ClientActiveTable: React.FC = () => {
  const [rows, setRows] = useState<ClientActiveRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      // 1. Récupérer tous les clients actifs
      const { data: clients, error } = await supabase
        .from('users')
        .select('id, name, email, is_active, permissions')
        .eq('role', 'client')
        .eq('is_active', true);
      if (error || !clients) {
        setRows([]);
        setLoading(false);
        return;
      }
      // 2. Pour chaque client, utiliser la fonction RPC pour obtenir les données financières précises
      const rows: ClientActiveRow[] = [];
      for (const client of clients) {
        // Récupérer les concours du client via permissions
        const contestIds = client.permissions?.contests || [];
        let totalCollecte = 0;
        let commissionVotAfrica = 0;
        
        if (contestIds.length > 0) {
          // Utiliser la fonction RPC comme dans ClientFinances
          const { data: contestsRevenue, error: contestsError } = await supabase.rpc('get_contests_revenue');
          
          if (!contestsError && contestsRevenue) {
            // Filtrer pour n'inclure que les concours du client
            const userContestsRevenue = contestsRevenue.filter((c: any) => contestIds.includes(c.id));
            
            // Récupérer les détails des concours pour avoir les taux de commission
            const { data: contestStats } = await supabase
              .from('contests')
              .select('id, commission_percentage')
              .in('id', contestIds);
              
            // Calculer comme dans ClientFinances
            let totalRevenue = 0;
            let totalCommission = 0;
            
            // Pour chaque concours, calculer le revenu ajusté et la commission
            for (const contest of userContestsRevenue) {
              const contestDetail = contestStats?.find((c: any) => c.id === contest.id);
              const rawRevenue = Number(contest.total_revenue) || 0;
              const transactionsCount = Number(contest.transactions_count) || 0;
              
              // Calculer le revenu ajusté avec la même fonction que dans ClientFinances
              const adjustedRevenue = calculateAdjustedRevenue(rawRevenue, transactionsCount);
              
              // Calculer la commission avec le taux du concours
              const commissionPercentage = contestDetail?.commission_percentage || 10;
              const commission = (adjustedRevenue * commissionPercentage) / 100;
              
              totalRevenue += adjustedRevenue;
              totalCommission += commission;
            }
            
            totalCollecte = totalRevenue;
            commissionVotAfrica = totalCommission;
          }
        }
        
        // Retraits du client - même logique que dans ClientFinances
        const { data: retraits } = await supabase
          .from('withdrawals')
          .select('amount, status')
          .eq('user_id', client.id);
          
        const totalRetraits = retraits
          ?.filter(w => w.status !== 'cancelled' && w.status !== 'rejected')
          .reduce((sum, w) => sum + Number(w.amount), 0) || 0;
          
        // Solde partenaire - même calcul que dans ClientFinances
        const soldePartenaire = totalCollecte - commissionVotAfrica - totalRetraits;
        
        rows.push({
          id: client.id,
          name: client.name || 'Sans nom',
          email: client.email,
          soldePartenaire,
          totalCollecte,
          commissionVotAfrica,
          totalRetraits
        });
      }
      setRows(rows);
      setLoading(false);
    };
    fetchAll();
  }, []);

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="animate-spin h-8 w-8 text-yellow-400" /></div>;
  }
  if (!rows.length) return <div className="text-center text-gray-400 py-8">Aucun client actif trouvé</div>;

  return (
    <div className="overflow-x-auto rounded-xl border border-white/10 bg-white/5 mb-8">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="bg-white/20">
            <th className="px-4 py-2 text-left text-white/90 font-medium">Nom</th>
            <th className="px-4 py-2 text-left text-white/90 font-medium">Email</th>
            <th className="px-4 py-2 text-right text-white/90 font-medium">Solde Partenaire</th>
            <th className="px-4 py-2 text-right text-white/90 font-medium">Total Collecté</th>
            <th className="px-4 py-2 text-right text-white/90 font-medium">Commission VotAfrica</th>
            <th className="px-4 py-2 text-right text-white/90 font-medium">Total Retraits</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id} className={`border-t border-white/10 hover:bg-white/10 ${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}>
              <td className="px-4 py-2 text-white font-semibold">{row.name}</td>
              <td className="px-4 py-2 text-white/80">{row.email}</td>
              <td className="px-4 py-2 text-right font-bold text-green-300">{formatCurrency(row.soldePartenaire)}</td>
              <td className="px-4 py-2 text-right text-blue-200">{formatCurrency(row.totalCollecte)}</td>
              <td className="px-4 py-2 text-right text-yellow-200">{formatCurrency(row.commissionVotAfrica)}</td>
              <td className="px-4 py-2 text-right text-purple-200">{formatCurrency(row.totalRetraits)}</td>
            </tr>
          ))}
          {/* Ligne de total */}
          <tr className="border-t-2 border-white/30 bg-white/10 font-bold">
            <td className="px-4 py-3 text-white" colSpan={2}>TOTAL</td>
            <td className="px-4 py-3 text-right text-green-300">
              {formatCurrency(rows.reduce((sum, row) => sum + row.soldePartenaire, 0))}
            </td>
            <td className="px-4 py-3 text-right text-blue-200">
              {formatCurrency(rows.reduce((sum, row) => sum + row.totalCollecte, 0))}
            </td>
            <td className="px-4 py-3 text-right text-yellow-200">
              {formatCurrency(rows.reduce((sum, row) => sum + row.commissionVotAfrica, 0))}
            </td>
            <td className="px-4 py-3 text-right text-purple-200">
              {formatCurrency(rows.reduce((sum, row) => sum + row.totalRetraits, 0))}
            </td>
          </tr>
        </tbody>
      </table>
      {/* Solde en caisse */}
      <div className="bg-white/10 p-4 border-t border-white/20 mt-4">
        <div className="flex justify-end">
          <div className="bg-gradient-to-r from-green-900/50 to-yellow-900/50 rounded-lg p-3 inline-flex items-center">
            <span className="text-white mr-2 font-semibold">Solde en caisse:</span>
            <span className="text-xl font-bold text-white">
              {formatCurrency(
                rows.reduce((sum, row) => sum + row.soldePartenaire, 0) + 
                rows.reduce((sum, row) => sum + row.commissionVotAfrica, 0)
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientActiveTable;
